(function initFulfillmentGate(global) {
  if (!global || global.EtFulfillmentGate) return;

  // The cart attributes as of the most recent cart response this page saw, or
  // null when we hold no trustworthy answer. validateForAction() runs on every
  // add-to-cart click and used to pay an unconditional GET /cart.js (~106ms,
  // serialized ahead of /cart/add.js) purely to re-read five attributes the
  // page had already been told about. This is that answer, kept from responses
  // the theme already receives.
  //
  // Deliberately `var`, not `let`: the Shopify CDN minifier hoists function
  // declarations above `let` bindings, so the first read becomes a TDZ
  // ReferenceError in production only (SHOPIFY-398).
  var knownCartAttributes = null;

  // Bumped every time the cache is invalidated. A response may refresh the cache
  // only while this still reads what it read at dispatch; otherwise a read that
  // started before a write could land after it and reinstate the pre-write
  // attributes. Not derivable from knownCartAttributes being null -- that read
  // would find a blank cache and refill it perfectly happily.
  var cartInvalidationEpoch = 0;

  // Every endpoint that can move the cart out from under the cache. All of them
  // blank it at dispatch; none of them has its response parsed.
  //
  // Not parsing the response is deliberate. Dawn attaches rendered `sections`
  // HTML to cart writes (assets/cart.js, cart-drawer.js, quick-add-bulk.js,
  // quick-order-list.js, and every add from product-form.js), so these payloads
  // run to hundreds of KB and the caller already parses them. A second parse of
  // the same bytes is exactly the kind of cost this change exists to remove.
  //
  // The add endpoint (either spelling) belongs here rather than among the
  // harmless reads, which is the opposite of what the Ajax cart API would lead
  // you to expect: update is the documented attributes endpoint, add takes only
  // line items. But
  // the add forms here carry cart attributes as hidden attributes[...] inputs --
  // four rendered in snippets/card-product.liquid, and all seven written at
  // runtime by syncFulfillmentAttributesToAddForms in assets/delivery-modal.js,
  // a strict superset of the five checked below. An add can rewrite every
  // attribute this gate validates.
  //
  // Getting this backwards fails silently: the cache and localStorage would
  // still agree, so the gate returns ok, skips the sync, and lets an item land
  // in a cart carrying the wrong location -- no throw, no Sentry event, just a
  // checkout that breaks later (the July 3 ZIP+4 incident). Treating a
  // non-mutating endpoint as mutating merely costs a redundant sync, so when in
  // doubt an endpoint belongs on this list.
  //
  // /cart/change.js and /cart/clear.js take no attributes, but emptying a cart
  // resets them -- which is why the theme re-applies attributes after a clear
  // (assets/delivery-modal.js) -- so they invalidate too.
  // Both spellings are live Ajax routes and both must be matched. Dawn posts to
  // `routes.cart_add_url` and friends, which render WITHOUT the suffix --
  // /cart/add, not /cart/add.js (layout/theme.liquid). Matching only the .js
  // form looks correct against hand-written call sites while being blind to
  // product-form.js, cart.js, cart-drawer.js, quick-add-bulk.js and
  // quick-order-list.js -- i.e. to the primary add-to-cart path this gate exists
  // to protect.
  //
  // Split by verb because they are not equally knowable:
  //   add    -- line items; leaves attributes alone unless the body sets them
  //   update -- the documented attributes endpoint
  //   reset  -- change/clear: take no attributes, but can empty the cart, and
  //             emptying resets them, so they are never projectable
  var CART_ADD_PATTERN = /\/cart\/add(\.js)?$/;
  var CART_UPDATE_PATTERN = /\/cart\/update(\.js)?$/;
  var CART_RESET_PATTERN = /\/cart\/(change|clear)(\.js)?$/;
  // Deliberately .js-only: bare /cart is the HTML cart page, not a cart payload.
  var CART_READ_PATTERN = /\/cart\.js$/;

  // What a write's own request body tells us it is about to set, taken as an
  // overlay on what the cache already holds.
  //
  // This assumes Shopify merges supplied attributes into the cart rather than
  // replacing the set. If that is ever wrong the cache errs toward claiming an
  // attribute the cart no longer has, which shows up as a mismatch and costs one
  // redundant sync -- never as an add waved through against the wrong cart. The
  // add form carries all five attributes checked here anyway, so the question
  // does not reach them.
  var ATTRIBUTE_FIELD_PATTERN = /^attributes\[(.+)\]$/;

  function normalizeString(value) {
    return value == null ? '' : String(value).trim();
  }

  function normalizeLocationId(value) {
    const raw = normalizeString(value);
    if (!raw) return '';
    if (global.EtInventoryDomain && typeof global.EtInventoryDomain.normalizeLocationId === 'function') {
      return global.EtInventoryDomain.normalizeLocationId(raw);
    }
    if (raw.indexOf('gid://shopify/Location/') !== -1) {
      const parts = raw.split('/');
      return parts[parts.length - 1] || raw;
    }
    return raw;
  }

  function normalizeTime(value) {
    const raw = normalizeString(value).toUpperCase();
    if (!raw) return '';

    const already24h = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (already24h) {
      const hours = String(parseInt(already24h[1], 10)).padStart(2, '0');
      return `${hours}:${already24h[2]}`;
    }

    const match = raw.match(/(\d{1,2})\s*:\s*(\d{2})\s*(AM|PM)/);
    if (!match) return raw.replace(/\s+/g, ' ');
    let hours = parseInt(match[1], 10);
    const minutes = match[2];
    const period = match[3];
    if (period === 'PM' && hours !== 12) hours += 12;
    if (period === 'AM' && hours === 12) hours = 0;
    return `${String(hours).padStart(2, '0')}:${minutes}`;
  }

  function getKey(constants, constantName, fallback) {
    return (constants && constants[constantName]) || fallback;
  }

  function getSelectionFromStorage(constants) {
    const cfg = constants || global.FulfillmentConstants || {};
    const zipKey = getKey(cfg, 'ZIP_CODE', 'selected_zip_code');
    const locationKey = getKey(cfg, 'LOCATION_ID', 'selected_location_id');
    const dateKey = getKey(cfg, 'DATE', 'selected_fulfillment_date');
    const typeKey = getKey(cfg, 'DELIVERY_TYPE', 'selected_delivery_type');
    const timeKey = getKey(cfg, 'TIME', 'selected_fulfillment_time');

    const deliveryType = normalizeString(global.localStorage.getItem(typeKey)).toLowerCase();
    return {
      zipCode: normalizeString(global.localStorage.getItem(zipKey)),
      locationId: normalizeLocationId(global.localStorage.getItem(locationKey)),
      fulfillmentDate: normalizeString(global.localStorage.getItem(dateKey)),
      deliveryType: deliveryType,
      fulfillmentTime: normalizeTime(global.localStorage.getItem(timeKey))
    };
  }

  function selectionHasRequiredFields(selection) {
    const hasBase =
      Boolean(selection.zipCode) &&
      Boolean(selection.locationId) &&
      Boolean(selection.fulfillmentDate) &&
      Boolean(selection.deliveryType);
    if (!hasBase) return false;
    if (selection.deliveryType !== 'delivery') return true;
    return Boolean(selection.fulfillmentTime);
  }

  function cartMatchesSelection(cart, selection) {
    const attrs = (cart && cart.attributes) || {};
    const cartSelection = {
      zipCode: normalizeString(attrs._zipCode),
      locationId: normalizeLocationId(attrs.LocationID),
      fulfillmentDate: normalizeString(attrs._selectedFulfillmentDate),
      deliveryType: normalizeString(attrs['Fulfillment Type']).toLowerCase(),
      fulfillmentTime: normalizeTime(attrs._selectedFulfillmentTime)
    };

    const baseMatches =
      cartSelection.zipCode === selection.zipCode &&
      cartSelection.locationId === selection.locationId &&
      cartSelection.fulfillmentDate === selection.fulfillmentDate &&
      cartSelection.deliveryType === selection.deliveryType;

    if (!baseMatches) return false;
    if (selection.deliveryType !== 'delivery') return true;
    return cartSelection.fulfillmentTime === selection.fulfillmentTime;
  }

  function resolveRequestUrl(resource) {
    if (typeof resource === 'string') return resource;
    if (resource && typeof resource.url === 'string') return resource.url;
    if (resource && typeof resource.href === 'string') return resource.href;
    return '';
  }

  // 'add' | 'update' | 'reset' | 'read' | null. Anchored at the path's end, so
  // it holds for a locale prefix (/en-us/cart/add) and for the absolute URLs a
  // Request object carries. The cheap indexOf reject runs first: this sits on
  // every fetch the page makes, cart or not.
  function classifyCartRequest(resource) {
    const url = resolveRequestUrl(resource);
    if (!url || url.indexOf('/cart') === -1) return null;
    const path = url.split('#')[0].split('?')[0];
    if (CART_ADD_PATTERN.test(path)) return 'add';
    if (CART_UPDATE_PATTERN.test(path)) return 'update';
    if (CART_RESET_PATTERN.test(path)) return 'reset';
    if (CART_READ_PATTERN.test(path)) return 'read';
    return null;
  }

  // The cart attributes a request is about to set, as read from its body:
  // an object (possibly empty, meaning "sets none"), or null for a body we
  // cannot inspect -- which is treated as "could set anything".
  function extractRequestAttributes(resource, init) {
    const body = init && init.body;
    if (body == null) {
      // A Request object carries its own body, and reading it means consuming
      // it. No call site in this theme builds one, but if one ever does it must
      // not be mistaken for a request that sets no attributes.
      const isRequest =
        resource &&
        typeof resource === 'object' &&
        typeof resource.url === 'string' &&
        typeof resource.method === 'string';
      return isRequest ? null : {};
    }

    // FormData (the card add-to-cart form) and URLSearchParams both iterate as
    // forEach(value, key). A File value cannot be an attribute, so it reads as
    // the empty string that clears one.
    if (typeof body.forEach === 'function') {
      const fromEntries = {};
      body.forEach(function (value, key) {
        const match = ATTRIBUTE_FIELD_PATTERN.exec(key);
        if (match) fromEntries[match[1]] = typeof value === 'string' ? value : '';
      });
      return fromEntries;
    }

    if (typeof body === 'string') {
      let parsed;
      try {
        parsed = JSON.parse(body);
      } catch (_error) {
        return null;
      }
      if (!parsed || typeof parsed !== 'object') return {};
      const attributes = parsed.attributes;
      if (!attributes || typeof attributes !== 'object') return {};
      return attributes;
    }

    // Blob, ArrayBuffer, ReadableStream: unreadable without consuming it.
    return null;
  }

  // What this request means for the cache, decided before it is sent.
  //   kind      -- 'read' refills from the response; 'mutate' invalidates now
  //                and refills from `projected`, never from the response
  //   projected -- the post-write attributes, when they are knowable: a warm
  //                cache merged with what the body sets. null means stay cold.
  // Returns null for a request that cannot move the cache at all.
  function planCartRequest(resource, init) {
    const kind = classifyCartRequest(resource);
    if (!kind) return null;
    if (kind === 'read') return { kind: 'read', projected: null };

    // change/clear carry no attributes and can empty the cart, which resets
    // them. Nothing about the request tells us the outcome.
    if (kind === 'reset') return { kind: 'mutate', projected: null };

    const overlay = extractRequestAttributes(resource, init);
    // A body we cannot read could be setting anything; a write onto a cache that
    // was already cold has nothing to build on. Either way, ask the server next.
    if (overlay === null || !knownCartAttributes) return { kind: 'mutate', projected: null };

    if (Object.keys(overlay).length === 0) {
      // An add that sets no attributes only appends line items, so the
      // attributes are unchanged -- this is every Recharge and drawer add, and
      // projecting it keeps them off the network. An update with no attributes
      // is carrying `updates` or `note`, and `updates` can empty the cart, so
      // that one still goes cold.
      return { kind: 'mutate', projected: kind === 'add' ? knownCartAttributes : null };
    }

    return { kind: 'mutate', projected: Object.assign({}, knownCartAttributes, overlay) };
  }

  // Insist on the cart shape, so a payload that merely happens to carry an
  // `attributes` key is never mistaken for a cart.
  function extractCartAttributes(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const attributes = payload.attributes;
    if (!attributes || typeof attributes !== 'object') return null;
    if (typeof payload.token !== 'string' && typeof payload.item_count !== 'number') return null;
    return attributes;
  }

  // Blanking and bumping always travel together: any refill still in flight was
  // computed against the attributes being discarded here.
  function invalidateKnownCart() {
    knownCartAttributes = null;
    cartInvalidationEpoch += 1;
  }

  // The localStorage keys getSelectionFromStorage reads, under whatever names
  // FulfillmentConstants gives them. A `storage` event on any of them means
  // another tab moved the selection.
  function isFulfillmentStorageKey(key) {
    const cfg = global.FulfillmentConstants || {};
    return (
      key === getKey(cfg, 'ZIP_CODE', 'selected_zip_code') ||
      key === getKey(cfg, 'LOCATION_ID', 'selected_location_id') ||
      key === getKey(cfg, 'DATE', 'selected_fulfillment_date') ||
      key === getKey(cfg, 'DELIVERY_TYPE', 'selected_delivery_type') ||
      key === getKey(cfg, 'TIME', 'selected_fulfillment_time')
    );
  }

  // Only ever refills the cache. A request that invalidates already blanked it
  // at dispatch, so a failed or unparseable response needs no handling of its
  // own -- it just leaves the cache cold and the next click reads /cart.js.
  function settleCartRequest(plan, epochAtDispatch, response) {
    // Something invalidated after this request started; refilling here would
    // reinstate exactly what that invalidation discarded.
    if (cartInvalidationEpoch !== epochAtDispatch) return;
    if (!response || !response.ok) return;

    if (plan.kind === 'mutate') {
      if (plan.projected) knownCartAttributes = plan.projected;
      return;
    }

    let clone;
    try {
      clone = response.clone();
    } catch (_error) {
      return;
    }
    clone.json().then(
      function (payload) {
        if (cartInvalidationEpoch !== epochAtDispatch) return;
        const attributes = extractCartAttributes(payload);
        if (attributes) knownCartAttributes = attributes;
      },
      function () {}
    );
  }

  // Every cart write in this theme goes through window.fetch, but it does so
  // from ~20 call sites across assets/, snippets/ and sections/. Observing at
  // the fetch layer is the only placement that cannot silently miss a call site
  // -- and a missed write means a stale cache waving through an add whose cart
  // is not carrying the shopper's date and location, which is the failure this
  // gate exists to prevent (the July 3 ZIP+4 incident).
  //
  // What it can still miss is a *spelling*. The endpoint set is closed, but only
  // once both the .js and bare forms are listed -- an earlier revision matched
  // only `.js` and was blind to every Dawn write while passing its whole suite,
  // because the tests used the same spelling the code did. Any change to the
  // patterns above needs a test in the spelling the theme actually sends.
  //
  // The idiomatic Dawn signal would be subscribe(PUB_SUB_EVENTS.cartUpdate).
  // It cannot serve here: eight call sites publish it, but syncAllAttributesToCart
  // -- the theme's primary attribute writer -- does not. The idiomatic channel is
  // blind to precisely the writes this gate cares about. If that writer is ever
  // made to publish, this wrapper can shrink to invalidation only, or go away.
  // Side effect worth knowing: et-sentry loads via a deferred loader, so
  // Sentry.init runs after this replaces window.fetch. Sentry's
  // supportsNativeFetch() fast path tests isNativeFunction(window.fetch), which
  // now fails, so it falls back to sniffing native fetch off a hidden iframe.
  // Breadcrumbs, spans and transport all still work -- it is one iframe at init,
  // not a defect. Noted so the next person debugging Sentry init does not chase
  // it.
  function installCartObserver() {
    if (typeof global.fetch !== 'function') return;
    const originalFetch = global.fetch;

    global.fetch = function etObservedFetch(resource, init) {
      let plan = null;
      try {
        plan = planCartRequest(resource, init);
      } catch (_error) {
        plan = null;
      }

      if (plan && plan.kind !== 'read') {
        // Synchronous, before the request is even dispatched: for as long as
        // the write is in flight the cart's attributes are genuinely unknown,
        // and a click landing in that window must go back to the network.
        invalidateKnownCart();
      }

      const result = originalFetch.apply(this, arguments);
      if (!plan || !result || typeof result.then !== 'function') return result;

      const epochAtDispatch = cartInvalidationEpoch;
      return result.then(function (response) {
        try {
          settleCartRequest(plan, epochAtDispatch, response);
        } catch (_error) {}
        return response;
      });
    };
  }

  // Another tab can move the fulfillment selection and write the cart without
  // this page seeing either request. Anything meaning "this tab was away" drops
  // the cache back to cold, which costs one /cart.js on the next interaction --
  // precisely the case where a remembered answer would be the wrong one.
  function watchForExternalCartChanges() {
    if (typeof global.addEventListener !== 'function') return;
    // Scoped to the fulfillment keys: `storage` fires for every key on the
    // origin, and an unrelated write in another tab is no reason to pay a read.
    global.addEventListener('storage', function (event) {
      if (!event || !event.key || isFulfillmentStorageKey(event.key)) invalidateKnownCart();
    });
    global.addEventListener('pageshow', function (event) {
      if (event && event.persisted) invalidateKnownCart();
    });
    const doc = global.document;
    if (doc && typeof doc.addEventListener === 'function') {
      doc.addEventListener('visibilitychange', function () {
        if (doc.visibilityState === 'visible') invalidateKnownCart();
      });
    }
  }

  async function fetchCart() {
    const response = await fetch('/cart.js', {
      headers: { Accept: 'application/json' }
    });
    if (!response.ok) {
      throw new Error(`/cart.js request failed with status ${response.status}`);
    }
    return response.json();
  }

  async function syncAndVerifyCartSelection(selection) {
    if (typeof global.syncAllAttributesToCart !== 'function') {
      return { ok: false, reason: 'sync_unavailable' };
    }
    try {
      await global.syncAllAttributesToCart({ force: true });
    } catch (error) {
      return { ok: false, reason: 'sync_failed', error: error };
    }

    try {
      const recheckCart = await fetchCart();
      const matches = cartMatchesSelection(recheckCart, selection);
      return {
        ok: matches,
        reason: matches ? 'synced' : 'cart_mismatch_after_sync',
        cart: recheckCart
      };
    } catch (error) {
      return { ok: false, reason: 'cart_fetch_failed_after_sync', error: error };
    }
  }

  async function validateForAction() {
    if (typeof global.ensureFreshFulfillmentSelection === 'function') {
      try {
        await global.ensureFreshFulfillmentSelection({ syncCart: true, rerenderSummary: true });
      } catch (_error) {}
    }

    const selection = getSelectionFromStorage();
    if (!selectionHasRequiredFields(selection)) {
      return { ok: false, reason: 'missing_selection', selection: selection };
    }

    // `source` is not read by any caller; it is what makes "this click cost no
    // network" checkable from the console and from tests.
    let cart;
    const cachedAttributes = knownCartAttributes;
    if (cachedAttributes) {
      // The cart is already carrying date, location and fulfillment type, and
      // the server is what told us so. Nothing left to ask. No `cart` comes back
      // because we hold attributes, not a cart -- the one caller that wants line
      // items (checkout readiness) fetches its own and falls back to it.
      if (cartMatchesSelection({ attributes: cachedAttributes }, selection)) {
        return {
          ok: true,
          reason: 'cart_matches_selection',
          source: 'cache',
          selection: selection,
          cartAttributes: cachedAttributes
        };
      }
      // A warm cache that disagrees still needs no read: sync-and-verify below
      // ends in a verified one regardless.
    } else {
      // Cold cache: nothing has told us what the cart carries, so ask before
      // assuming it needs rewriting.
      try {
        cart = await fetchCart();
      } catch (error) {
        return { ok: false, reason: 'cart_fetch_failed', selection: selection, error: error };
      }

      if (cartMatchesSelection(cart, selection)) {
        return {
          ok: true,
          reason: 'cart_matches_selection',
          source: 'network',
          selection: selection,
          cart: cart
        };
      }
    }

    const syncResult = await syncAndVerifyCartSelection(selection);
    if (!syncResult.ok) {
      return {
        ok: false,
        reason: syncResult.reason || 'cart_mismatch',
        selection: selection,
        cart: syncResult.cart || cart,
        error: syncResult.error
      };
    }

    return { ok: true, reason: 'cart_synced_and_verified', selection: selection, cart: syncResult.cart || cart };
  }

  installCartObserver();
  watchForExternalCartChanges();

  global.EtFulfillmentGate = {
    normalizeString: normalizeString,
    normalizeLocationId: normalizeLocationId,
    normalizeTime: normalizeTime,
    getSelectionFromStorage: getSelectionFromStorage,
    selectionHasRequiredFields: selectionHasRequiredFields,
    cartMatchesSelection: cartMatchesSelection,
    syncAndVerifyCartSelection: syncAndVerifyCartSelection,
    validateForAction: validateForAction,
    getKnownCartAttributes: function () {
      return knownCartAttributes;
    },
    invalidateKnownCart: invalidateKnownCart
  };
})(window);
